import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import {
  InventoryPanel,
  type InventoryView,
  type InventoryViewChip,
} from "../components/InventoryPanel";
import { ForwardDemandPanel } from "../components/ForwardDemandPanel";
import { ReorderSuggestionsPanel } from "../components/ReorderSuggestionsPanel";
import { ReadyBouquetsView } from "../components/inventory/ReadyBouquetsView";
import { ALL_SHOPS, InventoryShopPicker } from "../components/inventory/InventoryShopPicker";
import { ReceiveModal, type ReceivePreset } from "../components/inventory/ReceiveModal";
import { InventoryShopActions } from "../components/inventory/InventoryShopActions";
import type {
  BouquetBuildSummary,
  ForwardDemandView,
  InventoryBalance,
  ReorderSuggestion,
} from "../api/types";
import { NavIcon } from "../components/nav/NavIcon";
import { useAuth } from "../state/AuthContext";
import { forwardDemandCount } from "../utils/forwardDemandCount";
import { looksLikeBarcode } from "../utils/looksLikeBarcode";
import { purchaseMoneyText } from "../utils/purchaseMoneyText";
import { readyBouquetCount } from "../utils/readyBouquets";
import { shelfZonesOnly } from "../utils/shelfZones";
import type { DashboardOutletContext } from "./dashboardOutletContext";

type ShopAction = "receive" | "more";

export function InventoryPage() {
  const { hasRole, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locations } = useOutletContext<DashboardOutletContext>();
  const ownerScoped = hasRole("OWNER", "ADMIN");
  const locationId = searchParams.get("locationId") ?? undefined;

  /**
   * G-94 S2 — receiving happens here now, not one page away.
   *
   * `receivePreset` holds what the door was opened with: an item card passes
   * the item, the toolbar passes nothing, and G-83's `?receiveForStock=`
   * redirect is honoured here so create-then-receive lands on this page rather
   * than the shop dashboard it used to.
   */
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receivePreset, setReceivePreset] = useState<ReceivePreset>(null);
  /**
   * G-121 S3: what the last receive stored, in the server's figures — with a
   * with-IVA price, the person sees the IVA taken out rather than trusting it.
   */
  const [receivedNotice, setReceivedNotice] = useState<string | null>(null);
  const [reloadSignal, setReloadSignal] = useState(0);
  /** The rows the panel fetched, so the toolbar acts on the same list. */
  const [shopRows, setShopRows] = useState<InventoryBalance[]>([]);
  /** Distinguishes "not loaded yet" from "this shop's shelf is empty". */
  const [shopRowsLoaded, setShopRowsLoaded] = useState(false);
  /** Which view (ready bouquets, low stock, to order) replaces the list; null = the list. */
  const [activeView, setActiveView] = useState<InventoryView | null>(null);
  /** The "More actions" sheet (transfer, waste, consumables, statistics, history, schedule). */
  const [toolsOpen, setToolsOpen] = useState(false);
  /** The floating "Actions" menu. */
  const [actionsOpen, setActionsOpen] = useState(false);
  /** An action waiting for its shop: the "Which shop?" sheet is open, or the page is switching to the shop just picked. */
  const [shopQuestion, setShopQuestion] = useState<ShopAction | null>(null);
  const [pendingAction, setPendingAction] = useState<ShopAction | null>(null);
  useEffect(() => {
    if (!actionsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActionsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actionsOpen]);

  const receiveForStock = searchParams.get("receiveForStock");
  useEffect(() => {
    if (!receiveForStock) return;
    /**
     * A link that names the item but no shop used to do nothing at all — no
     * modal, no message, the param just sitting in the address bar
     * (/code-review, this branch). Fall back to the person's own shop, which
     * is the rule the rest of this page already follows; an owner with no
     * assigned shop still has to pick one — Receive appears once a shop is chosen.
     */
    if (!locationId) {
      const own = user?.assignedLocation?.id;
      if (!own) return;
      const params = new URLSearchParams(searchParams);
      params.set("locationId", own);
      setSearchParams(params, { replace: true });
      return;
    }
    setReceivePreset({ itemId: receiveForStock });
    setReceiveOpen(true);
  }, [receiveForStock, locationId, user?.assignedLocation?.id, searchParams, setSearchParams]);

  /**
   * G-113 S2: a Shopify order's "Transfer now" names the source shop
   * (locationId), the item, the destination and the quantity. The modal opens
   * prefilled once the shop's rows are loaded (the item picker only offers
   * what the shop holds).
   */
  const transferItem = searchParams.get("transferItem");
  const transferTo = searchParams.get("transferTo");
  const transferQty = searchParams.get("transferQty");
  const transferPreset = useMemo(
    () =>
      transferItem || transferTo
        ? {
            itemId: transferItem ?? undefined,
            toLocationId: transferTo ?? undefined,
            quantity: transferQty ?? undefined,
          }
        : null,
    [transferItem, transferTo, transferQty],
  );
  const clearTransferQuery = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("transferItem");
    params.delete("transferTo");
    params.delete("transferQty");
    setSearchParams(params, { replace: true });
  };

  /** Drop the param once acted on, so a refresh does not reopen the door. */
  const clearReceiveForStockQuery = () => {
    if (!searchParams.get("receiveForStock")) return;
    const params = new URLSearchParams(searchParams);
    params.delete("receiveForStock");
    setSearchParams(params, { replace: true });
  };

  /**
   * The shop in view: the one the URL names, else the ONLY active shop. A
   * manager or florist is assigned to one shop, so the switch never renders
   * and nothing writes `?locationId=`; without this fallback the page saw "no
   * shop chosen" for them and offered no Receive or shop actions at all.
   */
  const activeLocations = locations.filter((l) => l.isActive);
  const shop =
    locations.find((l) => l.id === locationId) ??
    (activeLocations.length === 1 ? activeLocations[0] : undefined);

  // Another shop is another shelf: the shop actions wait for its own rows
  // rather than acting on the previous view's (transfer and waste offer only
  // what the shop holds).
  useEffect(() => {
    setShopRows([]);
    setShopRowsLoaded(false);
  }, [locationId]);

  /** Receive (or open More actions) at the shop in view. Only state setters: stable. */
  const runShopAction = useCallback((action: ShopAction) => {
    if (action === "receive") {
      setReceivePreset(null);
      setReceivedNotice(null);
      setReceiveOpen(true);
    } else {
      setToolsOpen(true);
    }
  }, []);

  // The shop picked in "Which shop?" is now in view: carry on with the action.
  useEffect(() => {
    if (!pendingAction || !shop) return;
    setPendingAction(null);
    runShopAction(pendingAction);
  }, [pendingAction, shop, runShopAction]);

  /**
   * G-132 — the three views' data is fetched HERE, once, because each chip
   * needs its count before its view is open. The panels take the data as a
   * prop instead of asking again.
   */
  const canSeeForwardDemand = hasRole("OWNER", "MANAGER");

  // Ready bouquets: no shop for an owner on All; otherwise the shop (the
  // server scopes everyone else to their own whatever is asked).
  // Managers/florists are scoped server-side; owners need a concrete shop
  // before low-stock rows make sense.
  const showLowStock = !ownerScoped || Boolean(locationId);
  const [bouquets, setBouquets] = useState<BouquetBuildSummary[] | null>(null);
  const [bouquetsError, setBouquetsError] = useState<string | null>(null);
  const [lowRows, setLowRows] = useState<ReorderSuggestion[] | null>(null);
  // Another shop is another answer: drop the old one rather than show it (and
  // its chip counts) until the new one arrives. A plain refresh keeps what is
  // there, so nothing flickers.
  useEffect(() => {
    setBouquets(null);
    setLowRows(null);
  }, [locationId, showLowStock]);
  useEffect(() => {
    let cancelled = false;
    void api
      .listBouquetsInStock(locationId)
      .then((list) => {
        if (cancelled) return;
        setBouquets(list);
        setBouquetsError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setBouquets([]);
        setBouquetsError(err instanceof Error ? err.message : "Could not load ready bouquets.");
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, reloadSignal]);

  useEffect(() => {
    if (!showLowStock) return;
    let cancelled = false;
    void api
      .getReorderSuggestions(locationId)
      .then((rows) => {
        if (!cancelled) setLowRows(rows);
      })
      .catch(() => {
        if (!cancelled) setLowRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showLowStock, locationId, reloadSignal]);

  // Whole-business by nature (G-43): owners see it without picking a shop.
  const [forwardDemand, setForwardDemand] = useState<ForwardDemandView | null>(null);
  // A failed read must be visible: no chip would read as "nothing to order".
  const [forwardDemandError, setForwardDemandError] = useState<string | null>(null);
  useEffect(() => {
    if (!canSeeForwardDemand) return;
    let cancelled = false;
    void api
      .getForwardDemand()
      .then((view) => {
        if (cancelled) return;
        setForwardDemand(view);
        setForwardDemandError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setForwardDemand(null);
        setForwardDemandError(
          err instanceof Error ? err.message : "Could not check the scheduled orders.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [canSeeForwardDemand, reloadSignal]);

  const activeShops = useMemo(
    () => locations.filter((l) => l.isActive).map((l) => ({ id: l.id, name: l.name })),
    [locations],
  );
  /** What the shop in view holds, by item — shown beside each result in the receive search. */
  const stockByItem = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of shopRows) {
      const qty = Number.parseFloat(row.qtyOnHand);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      map.set(row.item.id, Math.round(((map.get(row.item.id) ?? 0) + qty) * 1000) / 1000);
    }
    return map;
  }, [shopRows]);
  const lowItemIds = useMemo(() => new Set((lowRows ?? []).map((r) => r.itemId)), [lowRows]);
  const toOrderCount = forwardDemand ? forwardDemandCount(forwardDemand) : 0;

  // "To order" shows only when it has something to say — the card it replaces
  // rendered nothing when no scheduled order needed stock — or when the read
  // failed, so the failure is something you can open and read.
  const viewChips: InventoryViewChip[] = [
    {
      id: "bouquets",
      label: "Ready bouquets",
      icon: "flower",
      count: bouquets ? readyBouquetCount(bouquets) : undefined,
    },
    ...(showLowStock
      ? [{ id: "low" as const, label: "Low", icon: "alert" as const, count: lowRows?.length }]
      : []),
    ...(toOrderCount > 0 || forwardDemandError
      ? [
          {
            id: "order" as const,
            label: "To order",
            icon: "cart" as const,
            count: forwardDemandError ? undefined : toOrderCount,
          },
        ]
      : []),
  ];
  // A view whose chip went away (another shop, a refresh) falls back to the list.
  const openView = viewChips.some((c) => c.id === activeView) ? activeView : null;

  /**
   * G-94 — picking a shop writes the URL, because three other things already
   * read it: `InventoryPanel`'s balance request, G-83's create-then-receive
   * redirect and G-92's phone action sheet. `replace` rather than push: a shop
   * choice is a view, and stepping back through six of them to leave the page
   * is not what Back is for.
   */
  const setShop = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === ALL_SHOPS) params.delete("locationId");
    else params.set("locationId", next);
    setSearchParams(params, { replace: true });
  };

  /**
   * G-83 — "create a new item and then she can receive that item" (owner).
   *
   * The second half already existed and nothing used it: `ItemCreatePage` reads
   * a `returnLocation` query param and, when it has one, sends you straight to
   * that shop with `?receiveForStock=<new item>` instead of back to the item
   * list. A built flow with no way in. This is the way in.
   *
   * Which shop: the one being viewed if the URL names one, otherwise the user's
   * own — which is what a manager always has. When NEITHER is known (an owner
   * looking at every shop at once) the param is omitted and the plain create
   * flow runs, because there is no honest answer to "receive it where?" and
   * guessing would put stock in the wrong shop.
   */
  const receiveIntoLocationId = locationId ?? user?.assignedLocation?.id;
  const createItemHref = (seed?: string) => {
    const params = new URLSearchParams();
    if (receiveIntoLocationId) params.set("returnLocation", receiveIntoLocationId);
    /**
     * G-94 — carry what was searched for into the wizard, so a scan that found
     * nothing does not have to be typed again. A code goes to the barcode
     * field, anything else to the name; `looksLikeBarcode` decides, and the
     * wizard still opens on its first question either way.
     */
    if (seed) params.set(looksLikeBarcode(seed) ? "barcode" : "name", seed);
    const qs = params.toString();
    return qs ? `/app/items/new?${qs}` : "/app/items/new";
  };
  /**
   * G-83. Gated to match the route it opens — `/app/items/new` is
   * `RoleGate allow={["OWNER","MANAGER"]}`, so showing this to a florist would
   * be a button that lands on a refusal. This repo has removed a dead
   * florist-facing button before (G-15); do not add another.
   */
  const canCreateItem = hasRole("OWNER", "MANAGER");

  /**
   * Both need a shop. On "All" the menu still offers them (owner, 2026-10-09)
   * and asks "Which shop?" first; picking one switches the page to that shop,
   * and the action carries on once the page is there (the effect above).
   */
  const chooseShopAction = (action: ShopAction) => {
    setActionsOpen(false);
    if (shop) runShopAction(action);
    else setShopQuestion(action);
  };

  const actionItems = [
    canCreateItem ? (
      <Link
        key="new"
        role="menuitem"
        className="inv-fab-item"
        to={createItemHref()}
        onClick={() => setActionsOpen(false)}
      >
        <NavIcon name="addSquare" size={18} />
        New item
      </Link>
    ) : null,
    activeLocations.length > 0 ? (
      <button
        key="receive"
        type="button"
        role="menuitem"
        className="inv-fab-item"
        onClick={() => chooseShopAction("receive")}
      >
        <NavIcon name="truck" size={18} />
        Receive
      </button>
    ) : null,
    activeLocations.length > 0 ? (
      <button
        key="more"
        type="button"
        role="menuitem"
        className="inv-fab-item"
        aria-haspopup="dialog"
        onClick={() => chooseShopAction("more")}
      >
        <NavIcon name="more" size={18} />
        More actions
      </button>
    ) : null,
  ].filter((item) => item !== null);

  return (
    <section className="page-stack inv-page">
      {/* The owner dropped the visible title (the sidebar says where you are);
          it stays for screen readers. The page opens on the search. */}
      <h2 className="visually-hidden">Inventory</h2>

      <InventoryShopPicker
        locations={locations}
        value={locationId ?? ALL_SHOPS}
        onChange={setShop}
      />

      {receivedNotice ? <div className="alert success">{receivedNotice}</div> : null}
      {/* G-94 S4: the verbs that used to live on the shop page, now a menu
          behind "More actions" in the floating Actions menu. Only with a shop chosen — none of
          them has an honest answer to "at which shop?" in the All-shops
          roll-up. */}
      {shop ? (
        <InventoryShopActions
          shop={shop}
          rows={shopRows}
          otherShops={locations.filter((l) => l.isActive && l.id !== shop.id)}
          open={toolsOpen}
          onClose={() => setToolsOpen(false)}
          onChanged={() => setReloadSignal((n) => n + 1)}
          transferPreset={shopRowsLoaded ? transferPreset : null}
          onTransferPresetDone={clearTransferQuery}
        />
      ) : null}
      <InventoryPanel
        viewChips={viewChips}
        activeView={openView}
        onActiveViewChange={setActiveView}
        lowItemIds={lowItemIds}
        renderView={(view, query) => {
          if (view === "bouquets") {
            return (
              <ReadyBouquetsView
                bouquets={bouquets}
                query={query}
                groupByShop={ownerScoped && !locationId}
                shops={activeShops}
                error={bouquetsError}
              />
            );
          }
          if (view === "low") {
            return lowRows && lowRows.length === 0 ? (
              <p className="muted inv-empty">Nothing is below its reorder threshold.</p>
            ) : (
              <ReorderSuggestionsPanel locationId={locationId} rows={lowRows ?? []} bare />
            );
          }
          if (forwardDemandError) return <div className="alert error">{forwardDemandError}</div>;
          return forwardDemand ? <ForwardDemandPanel view={forwardDemand} bare /> : null;
        }}
        reloadSignal={reloadSignal}
        onRowsChange={(rows) => {
          setShopRows(rows);
          setShopRowsLoaded(true);
        }}
        onReceive={
          shop
            ? (preset) => {
                setReceivePreset(preset);
                setReceivedNotice(null);
                setReceiveOpen(true);
              }
            : undefined
        }
        renderNoMatchAction={
          canCreateItem
            ? (query) => (
                /**
                 * G-94, owner 2026-09-17: a scan that matches nothing SUGGESTS
                 * a new item — it never creates one. This is a link to the
                 * wizard's first question with the code carried along; the
                 * person decides, and can still walk away.
                 */
                <Link className="button tinted" to={createItemHref(query)}>
                  {looksLikeBarcode(query)
                    ? `Create a new item with barcode ${query}`
                    : `Create “${query}” as a new item`}
                </Link>
              )
            : undefined
        }
      />

      {receiveOpen && shop ? (
        <ReceiveModal
          locationId={shop.id}
          zones={shelfZonesOnly(shop.zones)}
          preset={receivePreset}
          stockByItem={stockByItem}
          newItemHref={canCreateItem ? createItemHref : undefined}
          onClose={() => {
            setReceiveOpen(false);
            setReceivePreset(null);
            clearReceiveForStockQuery();
          }}
          onSuccess={({ itemName, batch }) => {
            setReceiveOpen(false);
            setReceivePreset(null);
            clearReceiveForStockQuery();
            setReloadSignal((n) => n + 1);
            const moneyLine = purchaseMoneyText({ ...batch, netTotalEur: batch.invoiceTotalEur });
            setReceivedNotice(`Received ${itemName} into stock${moneyLine ? `: ${moneyLine}` : "."}`);
          }}
        />
      ) : null}

      {shopQuestion ? (
        <div
          className="inv-sheet-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShopQuestion(null);
          }}
        >
          <div className="inv-sheet" role="dialog" aria-modal="true" aria-label="Which shop?">
            <div className="inv-sheet-title">Which shop?</div>
            <div className="inv-sheet-sub">
              {shopQuestion === "receive" ? "Receive stock into" : "More actions for"}
            </div>
            {activeLocations.map((loc) => (
              <button
                key={loc.id}
                type="button"
                className="inv-sheet-action"
                onClick={() => {
                  setPendingAction(shopQuestion);
                  setShopQuestion(null);
                  setShop(loc.id);
                }}
              >
                {loc.name}
              </button>
            ))}
            <button type="button" className="inv-sheet-close" onClick={() => setShopQuestion(null)}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {/*
        * The page's verbs, as one floating "Actions" button in the bottom-right
        * corner (owner, 2026-10-09), on every width. Open, it lists New item,
        * Receive and More actions (the shop tools sheet); on "All" the last two
        * ask "Which shop?" first.
        */}
      {actionItems.length > 0 ? (
        <div className="inv-fab">
          {actionsOpen ? (
            <div className="inv-fab-scrim" aria-hidden="true" onClick={() => setActionsOpen(false)} />
          ) : null}
          {actionsOpen ? (
            <div className="inv-fab-menu" id="inventory-actions-menu" role="menu">
              {actionItems}
            </div>
          ) : null}
          <button
            type="button"
            className="inv-fab-button"
            aria-haspopup="menu"
            aria-expanded={actionsOpen}
            aria-controls={actionsOpen ? "inventory-actions-menu" : undefined}
            onClick={() => setActionsOpen((open) => !open)}
          >
            <NavIcon name={actionsOpen ? "close" : "more"} size={18} />
            Actions
          </button>
        </div>
      ) : null}
    </section>
  );
}
